/**
 * Two colours in, a whole palette out.
 *
 * A clinic admin chooses a primary and a secondary and nothing else. Everything the UI
 * needs beyond those two -- the hover shade, the tinted background of a pill, the text
 * colour that sits on a filled button -- is derived here, so a clinic cannot end up with a
 * half-branded app where the buttons followed and the accents did not.
 *
 * Two consumers, and they need different things:
 *
 *   * `createAppTheme` needs **real hex**, because MUI's `alpha()` and `getContrastText()`
 *     parse the value. Give it `var(--ch-primary)` and both break.
 *   * The `*.styles.ts` files need **`var(--ch-*)` strings**, so they can stay the plain
 *     `as const` objects they are today -- no theme callback, no dependence on which of the
 *     three incompatible `Theme` types a given file imports. See the note in mixins.ts.
 *
 * So this module produces both from the same source, and `applyBrandTokens` publishes the
 * variables onto the document. The defaults in index.css mean the variables always resolve,
 * even before any JavaScript has run.
 */

/** Carefull's own. What an unbranded clinic, and every signed-out visitor, gets. */
export const CAREFULL_PRIMARY: string = '#004868';
export const CAREFULL_SECONDARY: string = '#FE8000';

/**
 * The ink used when a brand colour is too light to carry white text. The same near-navy
 * the body copy uses, so a button that flips to dark text matches the page rather than
 * introducing a second black.
 */
export const DARK_INK: string = '#1A1A2E';

/**
 * How far light/ and dark/ sit from the base, in HSL lightness.
 *
 * 0.12 is not arbitrary: Motion Care's hand-picked #2F7AC7 / #1E5A9E / #5A9AD9 trio sits at
 * lightness 0.482 / 0.369 / 0.602, so the shades they chose by eye are -0.113 and +0.120
 * away. Deriving at 0.12 reproduces that relationship for any hue, which is what lets
 * Motion Care's seeded colours render as the palette they already had.
 */
const LIGHTNESS_STEP: number = 0.12;

export interface BrandShade {
    main: string;
    light: string;
    dark: string;
    /** Whichever of white or the dark ink is legible on `main`. */
    contrastText: string;
    /** `"0, 72, 104"` -- for `rgba(var(--ch-primary-rgb), 0.08)`. */
    rgb: string;
}

export interface BrandTokens {
    primary: BrandShade;
    secondary: BrandShade;
}

interface Rgb {
    r: number;
    g: number;
    b: number;
}

// ---------------------------------------------------------------------------
// Colour maths
// ---------------------------------------------------------------------------

/** Returns null for anything that is not `#RRGGBB`, so a bad value falls back instead of throwing. */
const parseHex = (hex: string | null | undefined): Rgb | null => {
    if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
        return null;
    }

    return {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
    };
};

const toHex = ({ r, g, b }: Rgb): string => {
    const channel = (value: number): string =>
        Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, '0');

    return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
};

/**
 * Shifts a colour's HSL lightness, keeping hue and saturation.
 *
 * Done in HSL rather than by scaling the RGB channels because scaling drags a colour
 * towards black or white and washes the hue out on the way; a clinic's brand has to still
 * look like their brand at both ends.
 */
const shiftLightness = (rgb: Rgb, delta: number): Rgb => {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lightness = (max + min) / 2;

    if (max === min) {
        // Grey: no hue to preserve, so shift the channels together.
        const value = Math.min(1, Math.max(0, lightness + delta)) * 255;
        return { r: value, g: value, b: value };
    }

    const chroma = max - min;
    const saturation =
        lightness > 0.5 ? chroma / (2 - max - min) : chroma / (max + min);

    let hue: number;
    if (max === r) {
        hue = ((g - b) / chroma + (g < b ? 6 : 0)) / 6;
    } else if (max === g) {
        hue = ((b - r) / chroma + 2) / 6;
    } else {
        hue = ((r - g) / chroma + 4) / 6;
    }

    const shifted = Math.min(1, Math.max(0, lightness + delta));
    const q = shifted < 0.5 ? shifted * (1 + saturation) : shifted + saturation - shifted * saturation;
    const p = 2 * shifted - q;

    const toChannel = (t: number): number => {
        let value = t;
        if (value < 0) value += 1;
        if (value > 1) value -= 1;
        if (value < 1 / 6) return p + (q - p) * 6 * value;
        if (value < 1 / 2) return q;
        if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
        return p;
    };

    return {
        r: toChannel(hue + 1 / 3) * 255,
        g: toChannel(hue) * 255,
        b: toChannel(hue - 1 / 3) * 255,
    };
};

/** WCAG 2.1 relative luminance. */
const relativeLuminance = ({ r, g, b }: Rgb): number => {
    const linear = (channel: number): number => {
        const c = channel / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
};

/** WCAG 2.1 contrast ratio, 1 to 21. */
export const contrastRatio = (a: string, b: string): number => {
    const first = parseHex(a);
    const second = parseHex(b);

    if (!first || !second) {
        return 1;
    }

    const lighter = Math.max(relativeLuminance(first), relativeLuminance(second));
    const darker = Math.min(relativeLuminance(first), relativeLuminance(second));

    return (lighter + 0.05) / (darker + 0.05);
};

/**
 * White or dark ink, whichever is more legible on the given background.
 *
 * Computed rather than fixed. The old theme could hardcode white on the blue and near-navy
 * on the gold because it knew both colours; a clinic that picks a pale primary would
 * otherwise get white-on-pale buttons at around 1.7:1 -- the same failure chartPalette.ts
 * documents for the old brand gold.
 */
export const contrastTextFor = (background: string): string =>
    contrastRatio(background, '#FFFFFF') >= contrastRatio(background, DARK_INK)
        ? '#FFFFFF'
        : DARK_INK;

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

const shadeFor = (hex: string, fallback: string): BrandShade => {
    const rgb = parseHex(hex) ?? parseHex(fallback)!;
    const main = toHex(rgb);

    return {
        main,
        light: toHex(shiftLightness(rgb, LIGHTNESS_STEP)),
        dark: toHex(shiftLightness(rgb, -LIGHTNESS_STEP)),
        contrastText: contrastTextFor(main),
        rgb: `${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}`,
    };
};

/**
 * The full palette for a clinic, or Carefull's when it has chosen nothing.
 *
 * Each colour falls back independently: a clinic that set only a primary keeps Carefull's
 * orange rather than losing both. Anything that is not `#RRGGBB` is treated as unset --
 * the API refuses such values, so reaching this means something upstream is wrong and a
 * readable app beats a correct crash.
 */
export const deriveBrandTokens = (
    primary?: string | null,
    secondary?: string | null,
): BrandTokens => ({
    primary: shadeFor(primary ?? CAREFULL_PRIMARY, CAREFULL_PRIMARY),
    secondary: shadeFor(secondary ?? CAREFULL_SECONDARY, CAREFULL_SECONDARY),
});

/** The custom properties the `*.styles.ts` files read. Mirrors index.css's `:root`. */
export const brandCssVariables = (tokens: BrandTokens): Record<string, string> => ({
    '--ch-primary': tokens.primary.main,
    '--ch-primary-light': tokens.primary.light,
    '--ch-primary-dark': tokens.primary.dark,
    '--ch-primary-contrast': tokens.primary.contrastText,
    '--ch-primary-rgb': tokens.primary.rgb,
    '--ch-secondary': tokens.secondary.main,
    '--ch-secondary-light': tokens.secondary.light,
    '--ch-secondary-dark': tokens.secondary.dark,
    '--ch-secondary-contrast': tokens.secondary.contrastText,
    '--ch-secondary-rgb': tokens.secondary.rgb,
});

/**
 * Publishes the variables onto `<html>`.
 *
 * Setting properties rather than re-rendering: every `*.styles.ts` object already points at
 * `var(--ch-*)`, so the browser repaints without React knowing anything happened. Only the
 * MUI theme, which needs literal values, is rebuilt.
 */
export const applyBrandTokens = (tokens: BrandTokens): void => {
    const root = document.documentElement;

    Object.entries(brandCssVariables(tokens)).forEach(([name, value]) => {
        root.style.setProperty(name, value);
    });
};
